import { useNavigate, useSearchParams } from "@solidjs/router";
import { createEffect, createSignal, onCleanup, Show } from "solid-js";
import { type Me, toFrenchMobile } from "../../shared/api";
import { api, ApiFailure } from "../api";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { AUTH_ERROR, AUTH_HEADING, AUTH_LINK, AUTH_TEXT, TextField } from "../components/TextField";
import { nextStepFor, session } from "../state/session";

const MESSAGES: Record<string, string> = {
  INVALID_PHONE_NUMBER: "Saisissez un numéro de mobile français (06 ou 07).",
  ACCOUNT_LINK_FAILED: "Votre compte n'a pas pu être relié au service vélo. Contactez le support.",
  SMS_UNAVAILABLE: "L'envoi du SMS est momentanément impossible. Réessayez dans quelques secondes.",
  OTP_ALREADY_ACTIVE: "Un code vient de vous être envoyé. Patientez une minute.",
  TOO_MANY_REQUESTS: "Trop de demandes. Réessayez plus tard.",
  INVALID_OTP: "Code incorrect.",
  OTP_EXPIRED: "Ce code a expiré. Demandez un nouveau code.",
  NO_PENDING_OTP: "Aucun code en attente. Demandez un nouveau code.",
  TOO_MANY_ATTEMPTS: "Trop de tentatives. Demandez un nouveau code.",
  PHONE_NUMBER_ALREADY_USED:
    "Ce numéro est déjà associé à un autre compte Pony. Contactez le support Pony.",
};

/** Phone verification of the Pony account (status CREATED → SMS code → ACTIVE), before riding. */
export function Phone() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vehicle = () => (typeof params.vehicle === "string" ? params.vehicle : undefined);
  const [phone, setPhone] = createSignal("");
  const [code, setCode] = createSignal("");
  const [sentTo, setSentTo] = createSignal<string | undefined>(
    session.me()?.ponyStatus === "OTP_REQUIRED" ? session.me()?.phone : undefined,
  );
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string>();
  // Already verified on Pony (existing rider, or verified earlier): nothing to do here.
  createEffect(() => {
    const m = session.me.state === "ready" ? session.me() : undefined;
    if (m?.ponyStatus === "ACTIVE" && !busy())
      navigate(nextStepFor(m, vehicle()), { replace: true });
  });
  // Pony allows a new code 60 s after the previous one.
  const [cooldown, setCooldown] = createSignal(0);
  const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
  onCleanup(() => clearInterval(timer));

  const fail = (e: unknown) => {
    const code = e instanceof ApiFailure ? e.code : "INTERNAL";
    setError(
      (e instanceof ApiFailure && e.userMessage) ||
        MESSAGES[code] ||
        "Une erreur est survenue. Réessayez.",
    );
  };

  const send = async (e?: Event) => {
    e?.preventDefault();
    setError(undefined);
    if (!toFrenchMobile(phone())) return setError(MESSAGES.INVALID_PHONE_NUMBER);
    setBusy(true);
    try {
      const me = await api.post<Pick<Me, "ponyStatus" | "phone">>("/me/phone", { phone: phone() });
      setSentTo(me.phone);
      setCode("");
      setCooldown(60);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e?: Event) => {
    e?.preventDefault();
    if (busy()) return;
    setError(undefined);
    setBusy(true);
    try {
      await api.post("/me/phone/verify", { code: code().trim() });
      await session.refetch();
      navigate(nextStepFor(session.me(), vehicle()), { replace: true });
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  return (
    <main class="screen bg-surface">
      <AppBar title="Vérification du téléphone" back="/map" />
      <form
        class="screen-body px-[15px] pt-8 short:pt-4"
        onSubmit={(e) => (sentTo() ? verify(e) : send(e))}
      >
        <h1 class={AUTH_HEADING}>Vérifiez votre numéro de téléphone.</h1>
        <p class={AUTH_TEXT}>
          Pour rouler avec Pony, votre numéro doit être vérifié. Un code vous est envoyé par SMS.
        </p>
        <Show
          when={sentTo()}
          fallback={
            <TextField
              id="phone"
              label="Numéro de téléphone :"
              class="mt-6 short:mt-4"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              placeholder="06 12 34 56 78"
              value={phone()}
              onInput={(e) => setPhone(e.currentTarget.value)}
            />
          }
        >
          {(to) => (
            <>
              <TextField
                id="otp"
                label="Code reçu par SMS :"
                class="mt-6 short:mt-4"
                inputClass="tracking-[0.3em]"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code()}
                onInput={(e) => {
                  setCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 6));
                  // The iPhone numeric keypad has no return key: submit on the 6th digit.
                  if (code().length === 6) {
                    // Close the keyboard first, while the field still exists: the next screen must not
                    // inherit the keyboard's layout (iOS sends no focusout for a removed field).
                    e.currentTarget.blur();
                    void verify();
                  }
                }}
              />
              <p class="mt-2 text-xs leading-[1.25] tracking-[0.12px] text-text">
                Un code de vérification vous a été envoyé par SMS au {to()}.
              </p>
              <div class="mt-3 flex justify-between">
                <button type="button" class={AUTH_LINK} onClick={() => setSentTo(undefined)}>
                  Modifier le numéro
                </button>
                <button
                  type="button"
                  class={AUTH_LINK}
                  disabled={cooldown() > 0 || !phone()}
                  onClick={() => void send()}
                >
                  {cooldown() > 0 ? `Renvoyer le code (${cooldown()} s)` : "Renvoyer le code"}
                </button>
              </div>
            </>
          )}
        </Show>
        <Show when={error()}>
          {(m) => (
            <p class={AUTH_ERROR} role="alert">
              {m()}
            </p>
          )}
        </Show>
        <div class="screen-footer">
          <Button
            type="submit"
            loading={busy()}
            disabled={sentTo() ? code().length !== 6 : !phone().trim()}
          >
            {sentTo() ? "Confirmer" : "Recevoir le code"}
          </Button>
        </div>
      </form>
    </main>
  );
}
